import { useForm } from "@inertiajs/react";
import React, { useEffect } from "react";

import FormModal from "@/components/Modal/FormModal";
import TextFieldComponent from "@/components/TextField";

export type FacilityData = {
  id: number;
  facility_code: string;
  nickname?: string;
  initial_date: string;
  cpf: string;
};

type Props = {
  isOpen: boolean;
  consumerId: number | null;
  consumerCpf?: string;
  mode?: "create" | "edit";
  facility?: Partial<FacilityData>;
  onClose: () => void;
  onSaved?: (message: string) => void;
  onError?: (message: string) => void;
};

const currentMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
};

const dateValue = (date?: string) => date?.slice(0, 10) || currentMonth();

export default function CreateFacilityModal({
  isOpen,
  consumerId,
  consumerCpf = "",
  mode = "create",
  facility,
  onClose,
  onSaved,
  onError,
}: Props) {
  const isEdit = mode === "edit";
  const form = useForm({
    facility_code: facility?.facility_code || "",
    nickname: facility?.nickname || "",
    initial_date: dateValue(facility?.initial_date),
    cpf: facility?.cpf || consumerCpf,
  });
  const { setData } = form;

  useEffect(() => {
    if (!consumerId && !facility?.id) return;

    setData({
      facility_code: facility?.facility_code || "",
      nickname: facility?.nickname || "",
      initial_date: dateValue(facility?.initial_date),
      cpf: facility?.cpf || consumerCpf,
    });
  }, [consumerCpf, consumerId, facility, setData]);

  const submit = () => {
    const onSuccess = () => {
      form.reset();
      onClose();
      onSaved?.(
        isEdit
          ? "Unidade consumidora atualizada com sucesso."
          : "Unidade consumidora criada com sucesso.",
      );
    };
    const onRequestError = (errors: Record<string, string | string[]>) => {
      const message = Object.values(errors).flat().join(" ");
      onError?.(message || "Não foi possível salvar a unidade consumidora.");
    };

    if (isEdit && facility?.id) {
      form.patch(`/manager/facilities/${facility.id}`, { onSuccess, onError: onRequestError });
    } else if (consumerId) {
      form.post(`/manager/consumers/${consumerId}/facilities`, {
        onSuccess,
        onError: onRequestError,
      });
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Editar UC" : "Adicionar UC"}
      description="Cadastre os dados da unidade consumidora"
      submitButtonTitle={isEdit ? "Salvar alterações" : "Adicionar unidade"}
      onSubmit={submit}
      isSubmitting={form.processing}
      size="sm"
    >
      <div className="grid grid-cols-1 gap-4">
        <TextFieldComponent
          labelPosition="top"
          label="Código da instalação"
          required
          type="text"
          placeholder="Digite o código da instalação"
          value={form.data.facility_code}
          onChange={(event) => form.setData("facility_code", event.target.value)}
        />

        <TextFieldComponent
          labelPosition="top"
          label="Apelido"
          required
          type="text"
          placeholder="Ex.: Unidade matriz"
          value={form.data.nickname}
          onChange={(event) => form.setData("nickname", event.target.value)}
        />

        <TextFieldComponent
          labelPosition="top"
          label="Data de início para buscar as faturas"
          required
          type="date"
          value={form.data.initial_date}
          onChange={(event) => form.setData("initial_date", event.target.value)}
          footnote="Só será exibido para você"
        />

        <TextFieldComponent
          labelPosition="top"
          label="CPF ou CNPJ"
          required
          type="text"
          mask="cpf-cnpj"
          placeholder="000.000.000-00 ou 00.000.000/0000-00"
          value={form.data.cpf}
          onChange={(event) => form.setData("cpf", event.target.value)}
        />
      </div>
    </FormModal>
  );
}
